import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import {
  useForm,
  useFieldArray,
  UseFormReturn,
  useWatch
} from 'react-hook-form';
import { DateTime } from 'luxon';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useMessages } from 'modules/intl';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { TimeSignalClassification } from 'graphql/types';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { useFeatureFlag } from 'modules/common/hooks';
import {
  noteSchema,
  NOTE_FIELDS,
  NoteFormData,
  TimesheetLabelFields
} from '../../common/noteSchema';
import {
  defaultDateTime,
  mapFormFieldsToTimesheetLabels,
  mapTimesheetLabelsToFormFields,
  mergeResetLabels
} from '../../common/utils';

const errorMessagesKeys = [
  'note.error.time',
  'note.error.maxCommentLength',
  'note.error.fieldRequired',
  'note.error.startTimeRequired',
  'note.error.endTimeRequired',
  'note.error.endTimeShouldBeGreaterThanStartTime',
  'note.error.startAndEndTimeShouldNotBeEqual',
  'note.error.durationRequired',
  'note.error.durationShouldBeInStartTimeEndTimeRange'
];
const noteResolver = yupResolver(noteSchema(false));
const noteResolverNew = yupResolver(noteSchema(true));

export interface UseNoteFormResultProps {
  formMethods: UseFormReturn;
  errorMessage: (type: NOTE_FIELDS) => string | null | undefined;
  isValid: boolean;
  isFormDirty: boolean;
  timesheetLabelFields: TimesheetLabelFields;
}

type DefaultTimeValues = { startTime?: Date; endTime?: Date; date?: Date };
export const getDefaultValues = ({
  isSmartSuggestionEnabled,
  initialValues,
  timehseetLabels,
  defaultTimeValues
}: {
  isSmartSuggestionEnabled: boolean;
  initialValues?: NoteFormData;
  timehseetLabels: TimesheetLabel[];
  defaultTimeValues?: DefaultTimeValues;
}): NoteFormData => {
  const localDate = defaultDateTime();
  return (
    initialValues || {
      note: '',
      startTime: defaultTimeValues?.startTime ?? null,
      endTime: defaultTimeValues?.endTime ?? null,
      duration: 0,
      timesheetLabels: mapTimesheetLabelsToFormFields(
        isSmartSuggestionEnabled,
        timehseetLabels
      ),
      labelValuesInferenceId: undefined,
      customLabels: [],
      date:
        defaultTimeValues?.date ??
        DateTime.local(
          localDate.year,
          localDate.month,
          localDate.day
        ).toJSDate(),
      classification: TimeSignalClassification.Unclassified
    }
  );
};

export const useNoteForm = ({
  timesheetLabels,
  defaultTimeValues,
  initialValues,
  isEditDialog
}: {
  timesheetLabels: TimesheetLabel[];
  initialValues?: NoteFormData;
  defaultTimeValues?: DefaultTimeValues;
  isEditDialog?: boolean;
}): UseNoteFormResultProps => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();

  const defaultValues = useMemo(
    () =>
      getDefaultValues({
        isSmartSuggestionEnabled: isSmartSuggestionEnabled,
        initialValues: initialValues,
        timehseetLabels: timesheetLabels,
        defaultTimeValues
      }),
    [
      isSmartSuggestionEnabled,
      initialValues,
      timesheetLabels,
      defaultTimeValues
    ]
  );
  const isTiwDurationIndependentStartEndTime = useFeatureFlag(
    'isTiwDurationIndependentStartEndTime'
  );

  const formMethods = useForm({
    defaultValues,
    shouldUnregister: false,
    resolver: isTiwDurationIndependentStartEndTime
      ? noteResolverNew
      : noteResolver,
    mode: 'onChange'
  });
  const {
    getValues,
    setValue,
    formState,
    reset,
    trigger,
    control,
    handleSubmit
  } = formMethods;

  const { errors, isDirty, isValid, dirtyFields } = formState;

  const handleSubmitWithTransform = useCallback(
    (onValid, onInvalid) => {
      return handleSubmit(async (data: NoteFormData) => {
        const transformedLabelFields = mapFormFieldsToTimesheetLabels(
          data?.timesheetLabels
        );
        return onValid({
          ...data,
          timesheetLabels: transformedLabelFields
        });
      }, onInvalid);
    },
    [handleSubmit]
  );

  const { fields: timesheetFields, replace } = useFieldArray({
    control,
    name: NOTE_FIELDS.TIMESHEET_LABELS,
    keyName: 'key'
  });

  const startTime = useWatch({
    control,
    name: 'startTime',
    defaultValue: defaultValues.startTime
  });

  const endTime = useWatch({
    control,
    name: 'endTime',
    defaultValue: defaultValues.endTime
  });

  const duration = useWatch({
    control,
    name: 'duration',
    defaultValue: defaultValues.duration
  });

  const [isResetClicked, setIsResetClicked] = useState(false);

  useEffect(() => {
    if (startTime === null && endTime === null && duration === 0) {
      setIsResetClicked(true);
    } else if (startTime !== null && endTime !== null && duration == 0) {
      setIsResetClicked(false);
    } else if (duration !== 0) {
      setIsResetClicked(false);
    }
  }, [startTime, endTime, duration]);

  const previousDuration = useRef<number | undefined>(0);

  useEffect(() => {
    if (startTime && endTime) {
      const start = DateTime.fromJSDate(startTime);
      const end = DateTime.fromJSDate(endTime);
      const isDurationDifferent =
        isTiwDurationIndependentStartEndTime &&
        previousDuration.current !== duration;
      previousDuration.current = duration;
      if (
        ((isEditDialog && duration) || isDurationDifferent) &&
        !isResetClicked
      ) {
        setValue(`${NOTE_FIELDS.DURATION}`, duration);
      } else if (start <= end) {
        const duration = end.diff(start, ['hours', 'minutes', 'seconds']);
        const durationInSec = duration.shiftTo('seconds').toObject().seconds;
        setValue(
          `${NOTE_FIELDS.DURATION}`,
          durationInSec ? Math.round(durationInSec) : 0
        );
      } else {
        setValue(`${NOTE_FIELDS.DURATION}`, 0);
      }
      trigger();
    }
  }, [
    endTime,
    startTime,
    setValue,
    trigger,
    duration,
    isEditDialog,
    isTiwDurationIndependentStartEndTime,
    isResetClicked
  ]);

  useEffect(() => {
    // if timesheet labels popuplated/changed after initial values (e.g. date changed)
    const values = getValues();
    const resetLabels = mergeResetLabels(defaultValues, values);
    if (resetLabels) {
      if (values.timesheetLabels?.length) {
        replace(resetLabels);
      } else {
        // if add note is called before label query is returned
        reset(
          { ...values, [NOTE_FIELDS.TIMESHEET_LABELS]: resetLabels },
          {
            keepErrors: true, // errors will not be reset
            keepDirty: true // dirtyFields will not be reset
          }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues.timesheetLabels]);

  const errorMessages = useMessages(errorMessagesKeys);

  const memoizedErrors = useMemo(() => (isEmpty(errors) ? undefined : errors), [
    errors
  ]);

  const errorMessage = useCallback(
    (type: NOTE_FIELDS) =>
      memoizedErrors
        ? errorMessages[(memoizedErrors[type] as any)?.message ?? '']
        : '',
    [errorMessages, memoizedErrors]
  );

  const isFormDirty = useMemo(() => {
    return isDirty && !!Object.keys(dirtyFields).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(dirtyFields).length, isDirty]);

  return useMemo(
    () => ({
      formMethods: {
        ...formMethods,
        handleSubmit: handleSubmitWithTransform
      },
      errorMessage,
      isValid,
      isFormDirty,
      timesheetLabelFields: (timesheetFields as unknown) as Record<
        'key',
        TimesheetLabel
      >[]
    }),
    [
      errorMessage,
      formMethods,
      handleSubmitWithTransform,
      isFormDirty,
      isValid,
      timesheetFields
    ]
  );
};
